import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/stores/authStore';
import { getAuthErrorMessage } from '@/lib/authErrorMessage';

/**
 * Shared (non-themed) landing page for the email-verification link sent by
 * auth-service (`GET /api/v1/auth/verify-email/:token`). The link BookBed emails puts the token
 * in a query param (`?token=...`); the backend route takes it as a path segment, so this page
 * bridges that shape difference via `authService.verifyEmail`. Deliberately not themed like
 * Login/Register -- this is a low-frequency, transient confirmation screen, not brand-defining
 * real estate, so a single plain implementation avoids tripling the maintenance cost for no
 * meaningful UX gain.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { user } = useAuthStore();
  const [resendEmail, setResendEmail] = useState(user?.email ?? '');
  const [resendSent, setResendSent] = useState(false);

  const verify = useQuery({
    queryKey: ['verify-email', token],
    queryFn: () => authService.verifyEmail(token),
    enabled: !!token,
    retry: false,
  });

  const resend = useMutation({
    mutationFn: () => authService.resendVerification(resendEmail),
    onSuccess: () => setResendSent(true),
  });

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg border border-gray-100 text-center">
        {!token ? (
          <>
            <h1 className="text-xl font-bold text-gray-900">Invalid verification link</h1>
            <p className="mt-2 text-sm text-gray-500">This link is missing a verification token. Please use the link from your email, or request a new one below.</p>
          </>
        ) : verify.isLoading ? (
          <>
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500" aria-hidden="true" />
            <p className="mt-4 text-sm text-gray-500">Verifying your email…</p>
          </>
        ) : verify.isSuccess ? (
          <>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="mt-4 text-xl font-bold text-gray-900">Email verified</h1>
            <p className="mt-2 text-sm text-gray-500">Your email address has been confirmed.</p>
            <Link to="/auth/login" className="mt-6 inline-block text-sm text-primary-500 hover:text-primary-600 font-medium">
              Continue to sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold text-gray-900">Verification failed</h1>
            <p className="mt-2 text-sm text-gray-500">
              {getAuthErrorMessage(verify.error, 'This link is invalid or has expired.')}
            </p>

            {resendSent ? (
              <p className="mt-6 text-sm text-green-600">If an account exists for that email, a new verification link has been sent.</p>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); resend.mutate(); }}
                className="mt-6 space-y-3 text-left"
              >
                <label className="block text-sm font-medium text-gray-700" htmlFor="resend-email">
                  Resend verification email
                </label>
                <input
                  id="resend-email"
                  type="email"
                  required
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  type="submit"
                  disabled={resend.isPending}
                  className="w-full rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-50"
                >
                  {resend.isPending ? 'Sending…' : 'Resend link'}
                </button>
              </form>
            )}

            <Link to="/auth/login" className="mt-6 inline-block text-sm text-gray-500 hover:text-gray-700">
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
